import { inject, Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterEvent } from '@angular/router';
import {
  BehaviorSubject,
  filter,
  map,
  Observable,
  pairwise,
  pipe,
  Subject,
  takeUntil,
  UnaryFunction,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class RoutingService implements OnDestroy {
  private router: Router = inject(Router);

  private destroy$: Subject<void> = new Subject<void>();

  private skipQueryOperators: UnaryFunction<any, any>[] = [
    pairwise(),
    filter(
      ([
        prev,
        curr,
      ]: RouterEvent[]) => {
        const prevRoute: string = prev.url.split('?')[0];
        const currentRoute: string = curr.url.split('?')[0];

        return prevRoute !== currentRoute;
      },
    ),
    map(([_prev, curr]) => curr),
  ];

  private navigationEnd$: Observable<NavigationEnd> = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    takeUntil(this.destroy$),
  );

  private navigationStart$: Observable<NavigationStart> = this.router.events.pipe(
    filter(event => event instanceof NavigationStart),
    takeUntil(this.destroy$),
  );

  private previousRoute$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private currentRoute$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  constructor() {
    this.init();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public init(): void {
    this.listenNavigationEnd().subscribe(() => {
      this.previousRoute$.next(this.currentRoute$.value);
      this.currentRoute$.next(this.router.url);
    });
  }

  public getCurrentRoute(): string {
    return this.currentRoute$.value;
  }

  public getPreviousRoute(): string {
    return this.previousRoute$.value;
  }

  public getPreviousRoute$(): Observable<string | null> {
    return this.previousRoute$.asObservable();
  }

  public get getCurrentRoute$(): Observable<string | null> {
    return this.currentRoute$.asObservable();
  }

  public listenNavigationEnd(skipQueryChanges: boolean = false): Observable<NavigationEnd> {
    if (skipQueryChanges) {
      return this.navigationEnd$.pipe(
        pipe(this.skipQueryOperators[0], this.skipQueryOperators[1], this.skipQueryOperators[2]),
      );
    }

    return this.navigationEnd$;
  }

  public listenNavigationStart(skipQueryChanges: boolean = false): Observable<NavigationStart> {
    if (skipQueryChanges) {
      return this.navigationStart$.pipe(
        pipe(this.skipQueryOperators[0], this.skipQueryOperators[1], this.skipQueryOperators[2]),
      );
    }

    return this.navigationStart$;
  }
}
